import type { Language } from '../types/i18n'
import type { FilterKey } from './media'

export type MediaText = {
  categories: Record<FilterKey, string>
  photos: Record<string, { caption: string; alt: string }>
  reels: Record<string, { title: string; meta: string }>
  snippets: Record<string, { title: string; meta: string; description: string }>
}

export const mediaText: Record<Language, MediaText> = {
  nl: {
    categories: { all: 'Alles', portrait: 'Portret', fullbody: 'Full body' },
    photos: {
      headshot: {
        caption: 'Casting headshot',
        alt: 'Casting headshot van Fauve Willems in wit t-shirt',
      },
      'headshot-white': {
        caption: 'Portret · wit t-shirt',
        alt: 'Portret van Fauve Willems in wit t-shirt tegen grijze achtergrond',
      },
      'suit-portrait': {
        caption: 'Portret · zwart pak',
        alt: 'Portret van Fauve Willems in zwarte blazer',
      },
      bodysuit: {
        caption: 'Body · zwarte blazer',
        alt: 'Fauve Willems in zwarte body met blazer',
      },
      'suit-standing': {
        caption: 'Full body · zwart pak',
        alt: 'Fauve Willems staand in zwart pak, studioportret',
      },
      denim: {
        caption: 'Full body · denim',
        alt: 'Fauve Willems in wit shirt en lichte jeans, studioportret',
      },
      stool: {
        caption: 'Zittend · zwart pak',
        alt: 'Fauve Willems zittend op een kruk in zwart pak',
      },
    },
    reels: {
      'reel-dance': { title: 'Commercial / Jazz reel', meta: '0:58 · choreografie' },
      'reel-voice': { title: 'Zangreel · sopraan', meta: '1:12 · musical & pop' },
      'reel-acting': { title: 'Acteerreel · monoloog', meta: '1:04 · theater & camera' },
    },
    snippets: {
      'snippet-stage': {
        title: 'Eindpresentatie LMIPA',
        meta: 'Dance captain · solo',
        description:
          'Fragment uit de jaarlijkse eindpresentatie waarin ik als dance captain verantwoordelijk was voor verschillende stukken.',
      },
      'snippet-arena': {
        title: 'Katja en Babbette Discobal',
        meta: 'AFAS Live · nov 2025',
        description:
          'Ensemblewerk uit het Discobal in AFAS Live, met live band en volledige cast.',
      },
      'snippet-teaching': {
        title: 'Choreografie jongere afdeling',
        meta: 'LMIPA · docent dans',
        description:
          'Zelf gemaakte choreografie voor de jongere afdelingen van Lucia Marthas Institute for Performing Arts.',
      },
    },
  },

  en: {
    categories: { all: 'All', portrait: 'Portrait', fullbody: 'Full body' },
    photos: {
      headshot: {
        caption: 'Casting headshot',
        alt: 'Casting headshot of Fauve Willems in a white t-shirt',
      },
      'headshot-white': {
        caption: 'Portrait · white tee',
        alt: 'Portrait of Fauve Willems in a white t-shirt against a grey background',
      },
      'suit-portrait': {
        caption: 'Portrait · black suit',
        alt: 'Portrait of Fauve Willems in a black blazer',
      },
      bodysuit: {
        caption: 'Bodysuit · black blazer',
        alt: 'Fauve Willems in a black bodysuit with blazer',
      },
      'suit-standing': {
        caption: 'Full body · black suit',
        alt: 'Fauve Willems standing in a black suit, studio portrait',
      },
      denim: {
        caption: 'Full body · denim',
        alt: 'Fauve Willems in a white tee and light jeans, studio portrait',
      },
      stool: {
        caption: 'Seated · black suit',
        alt: 'Fauve Willems seated on a stool in a black suit',
      },
    },
    reels: {
      'reel-dance': { title: 'Commercial / jazz reel', meta: '0:58 · choreography' },
      'reel-voice': { title: 'Vocal reel · soprano', meta: '1:12 · musical & pop' },
      'reel-acting': { title: 'Acting reel · monologue', meta: '1:04 · theatre & camera' },
    },
    snippets: {
      'snippet-stage': {
        title: 'LMIPA showcase',
        meta: 'Dance captain · solo',
        description:
          'Excerpt from the annual showcase, where I was dance captain and responsible for several pieces.',
      },
      'snippet-arena': {
        title: 'Katja en Babbette Discobal',
        meta: 'AFAS Live · Nov 2025',
        description:
          'Ensemble work from the Discobal at AFAS Live, with a live band and full cast.',
      },
      'snippet-teaching': {
        title: 'Choreography for the younger department',
        meta: 'LMIPA · dance teacher',
        description:
          'Original choreography created for the younger departments of Lucia Marthas Institute for Performing Arts.',
      },
    },
  },

  de: {
    categories: { all: 'Alle', portrait: 'Porträt', fullbody: 'Ganzkörper' },
    photos: {
      headshot: {
        caption: 'Casting-Headshot',
        alt: 'Casting-Headshot von Fauve Willems im weißen T-Shirt',
      },
      'headshot-white': {
        caption: 'Porträt · weißes Shirt',
        alt: 'Porträt von Fauve Willems im weißen T-Shirt vor grauem Hintergrund',
      },
      'suit-portrait': {
        caption: 'Porträt · schwarzer Anzug',
        alt: 'Porträt von Fauve Willems im schwarzen Blazer',
      },
      bodysuit: {
        caption: 'Body · schwarzer Blazer',
        alt: 'Fauve Willems im schwarzen Body mit Blazer',
      },
      'suit-standing': {
        caption: 'Ganzkörper · schwarzer Anzug',
        alt: 'Fauve Willems stehend im schwarzen Anzug, Studioaufnahme',
      },
      denim: {
        caption: 'Ganzkörper · Denim',
        alt: 'Fauve Willems in weißem Shirt und heller Jeans, Studioaufnahme',
      },
      stool: {
        caption: 'Sitzend · schwarzer Anzug',
        alt: 'Fauve Willems sitzend auf einem Hocker im schwarzen Anzug',
      },
    },
    reels: {
      'reel-dance': { title: 'Commercial-/Jazz-Reel', meta: '0:58 · Choreografie' },
      'reel-voice': { title: 'Gesangs-Reel · Sopran', meta: '1:12 · Musical & Pop' },
      'reel-acting': { title: 'Schauspiel-Reel · Monolog', meta: '1:04 · Theater & Kamera' },
    },
    snippets: {
      'snippet-stage': {
        title: 'Abschlusspräsentation LMIPA',
        meta: 'Dance Captain · Solo',
        description:
          'Ausschnitt aus der jährlichen Abschlusspräsentation, bei der ich als Dance Captain für verschiedene Stücke verantwortlich war.',
      },
      'snippet-arena': {
        title: 'Katja en Babbette Discobal',
        meta: 'AFAS Live · Nov 2025',
        description:
          'Ensemblearbeit aus dem Discobal in AFAS Live, mit Live-Band und kompletter Besetzung.',
      },
      'snippet-teaching': {
        title: 'Choreografie für die jüngere Abteilung',
        meta: 'LMIPA · Tanzdozentin',
        description:
          'Eigene Choreografie für die jüngeren Abteilungen des Lucia Marthas Institute for Performing Arts.',
      },
    },
  },

  fr: {
    categories: { all: 'Tout', portrait: 'Portrait', fullbody: 'Plein pied' },
    photos: {
      headshot: {
        caption: 'Photo de casting',
        alt: 'Photo de casting de Fauve Willems en t-shirt blanc',
      },
      'headshot-white': {
        caption: 'Portrait · t-shirt blanc',
        alt: 'Portrait de Fauve Willems en t-shirt blanc sur fond gris',
      },
      'suit-portrait': {
        caption: 'Portrait · costume noir',
        alt: 'Portrait de Fauve Willems en blazer noir',
      },
      bodysuit: {
        caption: 'Body · blazer noir',
        alt: 'Fauve Willems en body noir avec blazer',
      },
      'suit-standing': {
        caption: 'Plein pied · costume noir',
        alt: 'Fauve Willems debout en costume noir, portrait studio',
      },
      denim: {
        caption: 'Plein pied · denim',
        alt: 'Fauve Willems en t-shirt blanc et jean clair, portrait studio',
      },
      stool: {
        caption: 'Assise · costume noir',
        alt: 'Fauve Willems assise sur un tabouret en costume noir',
      },
    },
    reels: {
      'reel-dance': { title: 'Reel commercial / jazz', meta: '0:58 · chorégraphie' },
      'reel-voice': { title: 'Reel chant · soprano', meta: '1:12 · musical & pop' },
      'reel-acting': { title: 'Reel jeu · monologue', meta: '1:04 · théâtre & caméra' },
    },
    snippets: {
      'snippet-stage': {
        title: 'Spectacle annuel LMIPA',
        meta: 'Dance captain · solo',
        description:
          "Extrait du spectacle annuel où j'étais dance captain, responsable de plusieurs pièces.",
      },
      'snippet-arena': {
        title: 'Katja en Babbette Discobal',
        meta: 'AFAS Live · nov. 2025',
        description:
          'Travail d’ensemble du Discobal à AFAS Live, avec orchestre live et distribution complète.',
      },
      'snippet-teaching': {
        title: 'Chorégraphie pour la section jeune',
        meta: 'LMIPA · professeure de danse',
        description:
          'Chorégraphie originale créée pour les sections plus jeunes du Lucia Marthas Institute for Performing Arts.',
      },
    },
  },

  es: {
    categories: { all: 'Todo', portrait: 'Retrato', fullbody: 'Cuerpo entero' },
    photos: {
      headshot: {
        caption: 'Foto de casting',
        alt: 'Foto de casting de Fauve Willems con camiseta blanca',
      },
      'headshot-white': {
        caption: 'Retrato · camiseta blanca',
        alt: 'Retrato de Fauve Willems con camiseta blanca sobre fondo gris',
      },
      'suit-portrait': {
        caption: 'Retrato · traje negro',
        alt: 'Retrato de Fauve Willems con blazer negro',
      },
      bodysuit: {
        caption: 'Body · blazer negro',
        alt: 'Fauve Willems con body negro y blazer',
      },
      'suit-standing': {
        caption: 'Cuerpo entero · traje negro',
        alt: 'Fauve Willems de pie con traje negro, retrato de estudio',
      },
      denim: {
        caption: 'Cuerpo entero · denim',
        alt: 'Fauve Willems con camiseta blanca y vaqueros claros, retrato de estudio',
      },
      stool: {
        caption: 'Sentada · traje negro',
        alt: 'Fauve Willems sentada en un taburete con traje negro',
      },
    },
    reels: {
      'reel-dance': { title: 'Reel commercial / jazz', meta: '0:58 · coreografía' },
      'reel-voice': { title: 'Reel de canto · soprano', meta: '1:12 · musical y pop' },
      'reel-acting': { title: 'Reel de interpretación · monólogo', meta: '1:04 · teatro y cámara' },
    },
    snippets: {
      'snippet-stage': {
        title: 'Presentación anual LMIPA',
        meta: 'Dance captain · solo',
        description:
          'Fragmento de la presentación anual en la que fui dance captain y responsable de varias piezas.',
      },
      'snippet-arena': {
        title: 'Katja en Babbette Discobal',
        meta: 'AFAS Live · nov. 2025',
        description:
          'Trabajo de ensemble del Discobal en AFAS Live, con banda en directo y reparto completo.',
      },
      'snippet-teaching': {
        title: 'Coreografía para el grupo joven',
        meta: 'LMIPA · profesora de danza',
        description:
          'Coreografía propia creada para los grupos más jóvenes del Lucia Marthas Institute for Performing Arts.',
      },
    },
  },
}
